/**
 * AI Agent Form
 *
 * Purpose: Hand-rolled form inputs for creating/editing an AI Agent Definition.
 *          Stateless — receives formData + onChange + errors from the parent
 *          modal. Mirrors WorkflowDefinitionModal's input style (NOT MetadataForm —
 *          AI Agent Definitions sit on the admin/metadata layer, not the
 *          business-data layer).
 * Author: Development Team
 * Created: 2026-05-23
 */

import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import { AiSkillOption } from '../../services/aiSkillService';
import { AgentKind, AgentSettings } from '../../services/aiAgentService';
import DescriptionField from '../common/DescriptionField';
import { renderMultiSelectOption } from '../common/MultiSelectOption';

export interface AIAgentFormData {
  ai_agent_id: string;
  tenant_id: string;
  label: string;
  /** Holds ai_skills.id UUIDs (ORCH-205). */
  skills: string[];
  tool_calls: string[];
  system_prompt: string;
  /** Typed per-agent config (ORCH-286 §5.2). */
  settings: AgentSettings;
  status: 'active' | 'disabled';
  access?: '0' | '1' | '2' | '3';
}

interface AIAgentFormProps {
  mode: 'create' | 'edit';
  formData: AIAgentFormData;
  errors: Record<string, string>;
  skillOptions: AiSkillOption[];
  toolCallOptions: string[];
  canEdit: boolean;
  showAccessLevel: boolean;
  onChange: (next: AIAgentFormData) => void;
}

const ACCESS_LEVELS: Array<{ value: '0' | '1' | '2' | '3'; label: string }> = [
  { value: '0', label: 'Internal Only' },
  { value: '1', label: 'Client Read-Only' },
  { value: '2', label: 'Client Overrideable' },
  { value: '3', label: 'Client Overrideable All' },
];

const AIAgentForm: React.FC<AIAgentFormProps> = ({
  mode,
  formData,
  errors,
  skillOptions,
  toolCallOptions,
  canEdit,
  showAccessLevel,
  onChange,
}) => {
  const set = (patch: Partial<AIAgentFormData>) => onChange({ ...formData, ...patch });
  const setSettings = (patch: Partial<AgentSettings>) =>
    onChange({ ...formData, settings: { ...formData.settings, ...patch } });
  const isProducer = formData.settings.kind === 'producer';
  const isReviewer = formData.settings.kind === 'reviewer';

  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="Name"
          required
          value={formData.label}
          onChange={(e) => set({ label: e.target.value })}
          error={!!errors.label}
          helperText={errors.label || 'Display name for the AI Agent'}
          fullWidth
          disabled={!canEdit}
          data-testid="ai-agent-label-input"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="AI Agent ID"
          value={formData.ai_agent_id}
          onChange={(e) => {
            if (mode === 'create') {
              set({ ai_agent_id: e.target.value });
            }
          }}
          error={!!errors.ai_agent_id}
          helperText={
            mode === 'create'
              ? errors.ai_agent_id || 'Auto-generated from name (editable)'
              : 'AI Agent ID (read-only)'
          }
          fullWidth
          disabled={mode === 'edit'}
          placeholder={mode === 'create' ? 'aa_example-agent' : undefined}
          data-testid="ai-agent-id-input"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <TextField
          label="System Prompt"
          value={formData.system_prompt}
          onChange={(e) => set({ system_prompt: e.target.value })}
          error={!!errors.system_prompt}
          helperText={errors.system_prompt || 'Base instruction set / role definition'}
          fullWidth
          multiline
          minRows={6}
          disabled={!canEdit}
          data-testid="ai-agent-system-prompt-input"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          renderOption={renderMultiSelectOption((option: { name: string }) => option.name)}
          options={skillOptions}
          // Map stored skill ids → option objects; unknown ids (orphaned
          // references) fall back to {id, name: id} so the raw id still shows.
          value={formData.skills.map(
            (id) => skillOptions.find((o) => o.id === id) ?? { id, name: id }
          )}
          onChange={(_e, newValue) => set({ skills: newValue.map((o) => o.id) })}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={!canEdit}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Skills"
              error={!!errors.skills}
              helperText={errors.skills || 'Select one or more skills'}
              data-testid="ai-agent-skills-input"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option.name} size="small" {...getTagProps({ index })} key={option.id} />
            ))
          }
          data-testid="ai-agent-skills-autocomplete"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          renderOption={renderMultiSelectOption((option: string) => option)}
          options={toolCallOptions}
          value={formData.tool_calls}
          onChange={(_e, newValue) => set({ tool_calls: newValue })}
          disabled={!canEdit}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tool Calls"
              error={!!errors.tool_calls}
              helperText={errors.tool_calls || 'Tools this agent is authorized to use'}
              data-testid="ai-agent-tool-calls-input"
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option}
                size="small"
                {...getTagProps({ index })}
                key={`${option}-${index}`}
              />
            ))
          }
          data-testid="ai-agent-tool-calls-autocomplete"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth disabled={!canEdit}>
          <InputLabel id="ai-agent-status-label">Status</InputLabel>
          <Select
            labelId="ai-agent-status-label"
            value={formData.status}
            label="Status"
            onChange={(e) => set({ status: e.target.value as AIAgentFormData['status'] })}
            data-testid="ai-agent-status-select"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="disabled">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {showAccessLevel && (
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth disabled={!canEdit}>
            <InputLabel id="ai-agent-access-label">Access Level</InputLabel>
            <Select
              labelId="ai-agent-access-label"
              value={formData.access || '2'}
              label="Access Level"
              onChange={(e) => set({ access: e.target.value as AIAgentFormData['access'] })}
              data-testid="ai-agent-access-select"
            >
              {ACCESS_LEVELS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
          Agent Settings
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormControl fullWidth disabled={!canEdit}>
          <InputLabel id="ai-agent-kind-label">Kind</InputLabel>
          <Select
            labelId="ai-agent-kind-label"
            value={formData.settings.kind}
            label="Kind"
            onChange={(e) => setSettings({ kind: e.target.value as AgentKind })}
            data-testid="ai-agent-kind-select"
          >
            <MenuItem value="producer">Producer</MenuItem>
            <MenuItem value="reviewer">Reviewer</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.settings.web_search}
              onChange={(e) => setSettings({ web_search: e.target.checked })}
              disabled={!canEdit}
              data-testid="ai-agent-web-search-switch"
            />
          }
          label="Web Search"
        />
      </Grid>

      {isProducer && (
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Max Tool Calls"
            type="number"
            value={formData.settings.max_tool_calls}
            onChange={(e) =>
              setSettings({ max_tool_calls: e.target.value === '' ? 0 : Number(e.target.value) })
            }
            error={!!errors['settings.max_tool_calls']}
            helperText={
              errors['settings.max_tool_calls'] || 'Tool-call budget for this producer (0-100)'
            }
            slotProps={{ htmlInput: { min: 0, max: 100 } }}
            fullWidth
            disabled={!canEdit}
            data-testid="ai-agent-max-tool-calls-input"
          />
        </Grid>
      )}

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          label="Output Section Heading"
          value={formData.settings.section_heading || ''}
          onChange={(e) => setSettings({ section_heading: e.target.value || null })}
          error={!!errors['settings.section_heading']}
          helperText={
            errors['settings.section_heading'] ||
            'Heading used when merged in a parallel step; defaults to the agent name'
          }
          fullWidth
          disabled={!canEdit}
          data-testid="ai-agent-section-heading-input"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Accordion data-testid="ai-agent-advanced-settings">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Advanced Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Phase Label"
                  value={formData.settings.phase_label || ''}
                  onChange={(e) => setSettings({ phase_label: e.target.value || null })}
                  error={!!errors['settings.phase_label']}
                  helperText={
                    errors['settings.phase_label'] ||
                    'Telemetry/event phase for a fan-out sub-agent (lowercase, digits, underscores)'
                  }
                  fullWidth
                  disabled={!canEdit}
                  data-testid="ai-agent-phase-label-input"
                />
              </Grid>

              {isReviewer && (
                <Grid size={{ xs: 12 }}>
                  <DescriptionField
                    label="Invalid-Output Feedback"
                    value={formData.settings.invalid_output_feedback || ''}
                    onChange={(value) => setSettings({ invalid_output_feedback: value || null })}
                    error={!!errors['settings.invalid_output_feedback']}
                    helperText={
                      errors['settings.invalid_output_feedback'] ||
                      'Verdict text returned when the model output fails to parse as JSON'
                    }
                    fullWidth
                    disabled={!canEdit}
                    data-testid="ai-agent-invalid-output-feedback-input"
                  />
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};

export default AIAgentForm;
